import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LayoutDashboard, List } from 'lucide-react'
import Section from 'app/frontend/base/components/Section'
import Card from 'app/frontend/base/components/Card'
import { useGuild } from 'app/frontend/base/config/context/guild'
import usePersistedHashValue from 'app/frontend/base/hooks/usePersistedHashValue'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import MyCandidacy from './components/MyCandidacy'
import CandidateSetup from './components/CandidateSetup'
import CandidateTable from './components/CandidateTable'
import CandidateStats from './components/CandidateStats'
import AddCandidateButton from './components/AddCandidateButton'
import AddAllToTeamButton from './components/AddAllToTeamButton'
import { useCandidates, useIsCandidateAdmin } from './hooks'

type CandidatesView = 'setup' | 'list'

const isCandidatesView = (value: string): value is CandidatesView =>
  value === 'setup' || value === 'list'

const GuildCandidates = () => {
  const { t } = useTranslation()
  const { guild, guildReference } = useGuild()
  const isAdmin = useIsCandidateAdmin()
  const { data } = useCandidates()
  const { value: activeView, setValue: setActiveView } =
    usePersistedHashValue<CandidatesView>({
      defaultValue: 'setup',
      storageKey: 'guild-candidates-activeView',
      isValid: isCandidatesView,
    })

  const candidates = useMemo(() => data?.candidates ?? [], [data])
  const approved = useMemo(
    () => candidates.filter((candidate) => candidate.status === 'approved'),
    [candidates],
  )
  const rejected = useMemo(
    () => candidates.filter((candidate) => candidate.status === 'rejected'),
    [candidates],
  )

  return (
    <Section
      title={`${t('shared.candidates')} | ${
        guild?.name ?? guildReference?.name ?? ''
      }`}
    >
      <Tabs
        value={activeView}
        onValueChange={(view) => isCandidatesView(view) && setActiveView(view)}
      >
        <h2 className='flex items-center gap-3 mb-4'>
          <span>{t('shared.candidates')}</span>
          <Card className='rounded-lg px-3 py-0 text-base'>
            {approved.length}
          </Card>
        </h2>

        {data && <MyCandidacy candidates={candidates} />}

        <div className='flex justify-end items-center gap-2 mb-3'>
          {isAdmin && <AddAllToTeamButton candidates={approved} />}
          {isAdmin && <AddCandidateButton />}
          <TabsList className='h-9'>
            <TabsTrigger value='setup'>
              <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                  <LayoutDashboard size={16} />
                </TooltipTrigger>
                <TooltipContent>
                  {t('dashboard.team.roster.setup-view')}
                </TooltipContent>
              </Tooltip>
            </TabsTrigger>
            <TabsTrigger value='list'>
              <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                  <List size={16} />
                </TooltipTrigger>
                <TooltipContent>
                  {t('dashboard.team.roster.list-view')}
                </TooltipContent>
              </Tooltip>
            </TabsTrigger>
          </TabsList>
        </div>

        <div className='flex gap-4 flex-col xl:flex-row mt-2'>
          <div className='flex-1 min-w-0'>
            <TabsContent value='setup' id='setup' className='mt-0'>
              <CandidateSetup candidates={approved} />
            </TabsContent>

            <TabsContent value='list' id='list' className='mt-0'>
              <CandidateTable candidates={approved} />
            </TabsContent>
          </div>

          <CandidateStats candidates={approved} />
        </div>
      </Tabs>

      {isAdmin && rejected.length > 0 && (
        <div className='mt-8'>
          <h4 className='mb-3'>Rejected</h4>
          <CandidateTable candidates={rejected} />
        </div>
      )}
    </Section>
  )
}

export default GuildCandidates
